// What a zone looks like: its painted ground, the props that stand on it (y-sorted with people), where light
// comes from at night, and a little ambient life. Everything is in the Inkwell style (render/inkscene.ts): the
// town square is rebuilt from its original layout, every other zone is painted from the art list its
// ZoneBuilder made (shared/zones.ts).
import ink from '../ink/ink.js';
import { T } from '../../../shared/zonebuild.js';
import { zoneDef } from '../../../shared/zones.js';
import { paintGround, zoneProps, townArt, propFor, type InkProp } from './inkscene.js';
import type { Light } from './sky.js';

const I = ink as any;
const TS = 16, K = 3;
export type Prop = InkProp;
export interface Scene {
  id: string; w: number; h: number;
  /** The painted ground, `gk` canvas pixels per game pixel. */
  ground: HTMLCanvasElement; gk: number;
  props: Prop[];
  lamps: Light[];
  /** The ground at one pixel per game pixel, with flat props (lily pads, boats) drawn in: the sightseeing
   *  camera's floor. */
  flat(): HTMLCanvasElement;
  /** Life drawn over everything: chimney smoke, butterflies, glints on the water, motes over the wayshrine. */
  ambient(ctx: CanvasRenderingContext2D, t: number, cx: number, cy: number, vw: number, vh: number): void;
  /** Small colour picture of the zone, one pixel per tile (minimap and world map). */
  thumb(): HTMLCanvasElement;
  /** Terrain per tile (T values), for the maps. */
  tiles: Uint8Array;
  /** Background colour beyond the edges. */
  edge: string;
}

const TILE_COLOR = ['#c2dc93', '#e6d5ad', '#ece2cd', '#8fc8dc', '#a77a52', '#c79a6a', '#efe3c0', '#a6c47c', '#d3cab9'];
TILE_COLOR[T.MOSS] = '#b9a77a';   // the Thornwood's moss paths
function makeThumb(w: number, h: number, tiles: Uint8Array, collision: string[], props: Prop[], mood?: string): HTMLCanvasElement {
  const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
  const g = cv.getContext('2d')!;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const t = tiles[y * w + x]; g.fillStyle = t === T.GRASS && mood === 'gloom' ? '#a9b98f' : TILE_COLOR[t] ?? '#c2dc93';
    if (t === T.GRASS && /[#1-9]/.test(collision[y]?.[x] ?? '')) g.fillStyle = '#94b46e';      // trees and bushes: darker green
    g.fillRect(x, y, 1, 1);
  }
  for (const pr of props) if (pr.house) { const b = pr.house; g.fillStyle = '#c9705e'; g.fillRect(b.x, b.y, b.w, b.h - 1); g.fillStyle = '#f3e6cc'; g.fillRect(b.x, b.y + b.h - 1, b.w, 1); }
  return cv;
}

function ambientFor(props: Prop[], tiles: Uint8Array, w: number, h: number, mood?: string) {
  const W = w * TS, H = h * TS;
  const chimneys = props.filter(p => p.chimney).map(p => p.chimney!);
  const shrines = props.filter(p => p.kind === 'wayshrine');
  const flies = mood && mood !== 'moss' ? [] : Array.from({ length: Math.max(3, Math.round(W * H / 70000)) }, (_, i) => ({ hx: (40 + I.hash(i, 61) * (W - 80)) * K, hy: (40 + I.hash(i, 62) * (H - 80)) * K, t: i * 3.1, col: ['#f7dd7a', '#ffffff', '#c9b8f0', '#f6c1cf'][i % 4] }));
  const motes = mood === 'gloom' ? Array.from({ length: 24 }, (_, i) => ({ x: I.hash(i, 71) * W, y: I.hash(i, 72) * H, p: i * 0.7 })) : [];
  const tileAt = (x: number, y: number) => x < 0 || y < 0 || x >= W || y >= H ? -1 : tiles[Math.floor(y / TS) * w + Math.floor(x / TS)];
  const vis = (x: number, y: number, cx: number, cy: number, vw: number, vh: number, m = 30) => x > cx - m && x < cx + vw + m && y > cy - m && y < cy + vh + m;
  return (g: CanvasRenderingContext2D, t: number, cx: number, cy: number, vw: number, vh: number) => {
    // glints on the water
    for (let i = 0; i < 46; i++) {
      const x = cx + I.hash(i, 11) * vw + Math.sin(t * 0.6 + i) * 3, y = cy + I.hash(i, 12) * vh;
      if (tileAt(x, y) !== T.WATER || tileAt(x + 6, y) !== T.WATER || tileAt(x, y + 6) !== T.WATER) continue;
      const ph = (t * 0.5 + I.hash(i, 13)) % 1; if (ph > 0.6) continue;
      g.strokeStyle = `rgba(255,255,255,${0.8 * Math.sin(ph / 0.6 * Math.PI)})`; g.lineWidth = 0.7; g.beginPath(); g.arc(x, y, 2.4, 0.2 * Math.PI, 0.8 * Math.PI); g.stroke();
    }
    g.save(); g.scale(1 / K, 1 / K);
    for (const [hx, hy] of chimneys) if (vis(hx, hy, cx, cy - 30, vw, vh + 30)) I.drawSmoke(g, hx * K, hy * K, t, Math.round(hx + hy) % 97, 1);
    if (flies.length) { I.st.flies = flies.filter(f => vis(f.hx / K, f.hy / K, cx, cy, vw, vh, 60)); I.drawButterflies(g); }
    g.restore();
    for (const s of shrines) if (vis(s.x, s.y, cx, cy, vw, vh)) for (let k = 0; k < 4; k++) {
      const ph = (t * 0.45 + k / 4) % 1; g.fillStyle = `rgba(214,240,255,${1 - ph})`;
      g.beginPath(); g.arc(s.x - 4 + k * 2.6 + Math.sin(t * 2 + k) * 2, s.y - 40 - ph * 18, 0.9, 0, Math.PI * 2); g.fill();
    }
    for (const m of motes) {   // pale wisps drifting over the graves
      const x = m.x + Math.sin(t * 0.4 + m.p) * 14, y = m.y + Math.cos(t * 0.3 + m.p * 2) * 8;
      if (!vis(x, y, cx, cy, vw, vh)) continue;
      g.fillStyle = `rgba(232,226,255,${0.35 + Math.sin(t * 2 + m.p) * 0.2})`; g.beginPath(); g.arc(x, y, 1.2, 0, Math.PI * 2); g.fill();
    }
  };
}

const cache = new Map<string, Scene>();
function build(id: string, w: number, h: number, tiles: Uint8Array, props: Prop[], lamps: Light[], collision: string[], mood?: 'gloom' | 'ember' | 'moss' | 'mist' | 'castle'): Scene {
  const seed = I.strSeed(id) % 100000;
  let painted: ReturnType<typeof paintGround> | null = null;
  const paint = () => painted ??= paintGround(w, h, tiles, mood, seed);   // on first look: the maps only need the thumbnail
  props.sort((a, b) => a.y - b.y);
  let thumb: HTMLCanvasElement | null = null, flat: HTMLCanvasElement | null = null;
  return {
    id, w, h, get ground() { return paint().cv; }, get gk() { return paint().k; }, props, lamps, tiles, edge: mood === 'gloom' ? '#a9b98f' : mood === 'ember' ? '#d8cdb6' : '#c2dc93',
    ambient: ambientFor(props, tiles, w, h, mood),
    thumb: () => thumb ??= makeThumb(w, h, tiles, collision, props, mood),
    flat: () => {
      if (flat) return flat;
      flat = document.createElement('canvas'); flat.width = w * TS; flat.height = h * TS;
      const g = flat.getContext('2d')!; g.drawImage(paint().cv, 0, 0, flat.width, flat.height);
      for (const p of props) if (p.flat && p.spr) g.drawImage(p.spr.c, p.x - p.spr.ox / K, p.y - p.spr.oy / K, p.spr.w / K, p.spr.h / K);
      return flat;
    },
  };
}

function townScene(collision: string[], w: number, h: number): Scene {
  const art = townArt(), lamps: Light[] = [], props: Prop[] = [];
  for (const p of art.props) props.push(...propFor(p, lamps));
  props.push(...art.extra);
  const ws = zoneDef('town')?.wayshrine;
  if (ws) props.push(...propFor({ kind: 'wayshrine', x: ws[0], y: ws[1] }, lamps));
  return build('town', w, h, art.tiles, props, lamps, collision);
}

/** Keep only a few zones painted at once (the grounds are big pictures). */
const KEEP = 6;
/** The scene for an outdoor zone (null for rooms, which have their own drawing). Built once, on first visit. */
export function sceneFor(id: string, collision: string[], w: number, h: number): Scene | null {
  const hit = cache.get(id); if (hit) { cache.delete(id); cache.set(id, hit); return hit; }
  let sc: Scene | null = null;
  if (id === 'town') sc = townScene(collision, w, h);
  else {
    const def = zoneDef(id); if (!def?.art) return null;
    const art = def.art, lamps: Light[] = [];
    sc = build(id, w, h, Uint8Array.from(art.ground, ch => +ch), zoneProps(art, lamps), lamps, collision, art.mood);
  }
  cache.set(id, sc);
  while (cache.size > KEEP) cache.delete(cache.keys().next().value!);
  return sc;
}
